/* ──────────────────────────────────────────────────────────────
   NewTypeWizard · reusable "create an item type" flow
   Step 1: pick a template  →  Step 2: customise (rename, image,
   add / edit / delete fields)  →  create + success confirmation.

   Drop it into any screen:
     <NewTypeWizard open={open} onClose={...} onCreated={(t) => ...} />
   ────────────────────────────────────────────────────────────── */

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  Pressable,
  Platform,
  Image,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import {
  Icon,
  Button,
  Chip,
  Input,
  Switch,
  Modal,
  Alert,
  Empty,
  SuccessDialog,
} from './ui';
import { SK, Template, TemplateField } from '../data/mockData';
import { useFieldTypes } from '../hooks/useFieldTypes';
import { createItemType, addItemTypeField, uploadTypeImage, getItemTypes, getItemTypeFields } from '../services/catalog/itemType';
import type { ItemType } from '../schemas/catalog/itemType';
import { handleApiError } from '../utils/errorHandler';

/* ── Field types ───────────────────────────────────────────────
   The field-type catalog is DB-backed and read via useFieldTypes() inside the
   component. The wizard shows each type's friendlier label (e.g. "Price",
   "Image") rather than the technical one. */

/* ── Field-key derivation (backend wants snake_case, letter-first) ── */

const slugify = (s: string) => {
  const base = s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (!base) return 'field';
  return /^[a-z]/.test(base) ? base : `f_${base}`;
};
// Unique key within one create pass (the backend enforces uniqueness per type).
const uniqueKey = (seen: Set<string>, label: string) => {
  const base = slugify(label);
  let cand = base;
  let n = 2;
  while (seen.has(cand)) cand = `${base}_${n++}`;
  seen.add(cand);
  return cand;
};

/* ── Local draft model ───────────────────────────────────────── */

interface DraftField {
  uid: number;
  label: string;
  type: string;
  required: boolean;
  // For Choices / Multi-select — the option values the end user can pick from.
  // UI-only: drives the live example & preview (not sent to the backend).
  options?: string[];
}

// Types that present a fixed set of choices (need an options editor).
const CHOICE_TYPES = new Set(['ENUM', 'MULTI_SELECT']);

interface CreateResult {
  created: ItemType;
  total: number;
  ok: number;
  failed: string[];
}

export interface NewTypeWizardProps {
  open: boolean;
  onClose: () => void;
  // Fires after a successful create. `manage` is true when the user chose
  // "Manage fields" from the success dialog (open the field builder next).
  onCreated: (type: ItemType, opts?: { manage?: boolean }) => void;
}

export default function NewTypeWizard({ open, onClose, onCreated }: NewTypeWizardProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  // DB-backed field-type catalog (falls back to the local registry).
  const fieldTypes = useFieldTypes();
  const FRIENDLY_TYPES = fieldTypes.friendly;
  const ADVANCED_TYPES = fieldTypes.advanced;
  // Wizard shows the friendly label for a type.
  const metaFor = (t: string) => {
    const m = fieldTypes.meta(t);
    return { label: m.friendlyLabel, icon: m.icon };
  };

  // Responsive breakpoints for the 3-panel customise layout.
  const { width: winW, height: winH, isDesktop } = useResponsive();
  const threeCol = isDesktop;      // Example | Fields | Preview side-by-side (>= 1024)
  const stackRow = winW < 560;     // field card stacks name above its controls
  // Height the columns fill so the full-screen page has no dead space; the left
  // editor panel is pinned to this height and sticks while the centre scrolls.
  const colHeight = Math.max(460, winH - 188);

  const uidRef = useRef(1);
  const nextUid = () => uidRef.current++;

  const [step, setStep] = useState<1 | 2>(1);
  const [selId, setSelId] = useState<string | null>(null);
  // Selected existing type (the tenant's own types, usable as templates).
  const [selTypeId, setSelTypeId] = useState<string | null>(null);
  const [myTypes, setMyTypes] = useState<ItemType[]>([]);
  const [preparing, setPreparing] = useState(false);

  // Draft (step 2)
  const [name, setName] = useState('');
  const [fields, setFields] = useState<DraftField[]>([]);
  const [imageKey, setImageKey] = useState<string | undefined>();
  const [imagePreview, setImagePreview] = useState<string | undefined>();
  const [imageBusy, setImageBusy] = useState(false);

  // The field whose live example shows in the left panel (click to focus).
  const [activeUid, setActiveUid] = useState<number | null>(null);

  // Sub-dialogs
  const [typePickerUid, setTypePickerUid] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [delUid, setDelUid] = useState<number | null>(null);

  const [creating, setCreating] = useState(false);
  // Synchronous re-entry guard: `creating` state updates async, so two fast
  // clicks can both pass the state check before a re-render and create twice.
  const creatingRef = useRef(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);

  // Reset everything whenever the wizard (re)opens, and load the tenant's
  // existing types so they can be reused as templates (with their DB images).
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelId(null);
      setSelTypeId(null);
      setName('');
      setFields([]);
      setImageKey(undefined);
      setImagePreview(undefined);
      setImageBusy(false);
      setActiveUid(null);
      setTypePickerUid(null);
      setShowAdvanced(false);
      setDelUid(null);
      setCreating(false);
      setConfirmCreate(false);
      setResult(null);
      getItemTypes()
        .then(res => setMyTypes(res.items ?? []))
        .catch(() => setMyTypes([]));
    }
  }, [open]);

  /* ── Step 1 → 2 ─────────────────────────────────────────── */

  // Every type gets an Image (MEDIA) field by default so items can upload a
  // picture that shows on the storefront / public catalog.
  const withImageField = (list: DraftField[]): DraftField[] =>
    list.some(f => f.type === 'MEDIA')
      ? list
      : [{ uid: nextUid(), label: 'Image', type: 'MEDIA', required: false }, ...list];

  const continueToCustomize = async () => {
    // Branch A — a starter template (static field set).
    if (selId) {
      const tpl = SK.templates.find((t: Template) => t.id === selId);
      if (!tpl) return;
      setName(tpl.name);
      setFields(
        withImageField(
          (tpl.fields as TemplateField[]).map(f => ({
            uid: nextUid(),
            label: f.label,
            type: f.type,
            required: !!f.required,
          })),
        ),
      );
      setStep(2);
      return;
    }
    // Branch B — one of the tenant's existing types: pull its real fields and
    // carry its image so the new type starts as a copy the user can tweak.
    if (selTypeId) {
      const src = myTypes.find(t => t.id === selTypeId);
      if (!src) return;
      setPreparing(true);
      try {
        const res = await getItemTypeFields(selTypeId);
        setName(src.name);
        setFields(
          withImageField(
            (res.items ?? []).map(fd => ({
              uid: nextUid(),
              label: fd.fieldKey,
              type: fd.fieldType,
              required: !!fd.isRequired,
            })),
          ),
        );
        // Reuse the source type's image (read-only reference to the same object).
        setImageKey((src as any).imageKey ?? undefined);
        setImagePreview((src as any).imageUrl ?? undefined);
        setStep(2);
      } catch (err) {
        handleApiError(err, 'Could not load that type');
      } finally {
        setPreparing(false);
      }
    }
  };

  /* ── Field draft ops ────────────────────────────────────── */

  const addField = () => {
    const uid = nextUid();
    setFields(prev => [...prev, { uid, label: '', type: 'TEXT', required: false }]);
    setActiveUid(uid); // focus the new field in the example panel
  };

  const patchField = (uid: number, patch: Partial<DraftField>) =>
    setFields(prev => prev.map(f => (f.uid === uid ? { ...f, ...patch } : f)));

  const removeField = (uid: number) => {
    setFields(prev => prev.filter(f => f.uid !== uid));
    setActiveUid(cur => (cur === uid ? null : cur));
  };

  // ── Choices / Multi-select option editing (UI-only) ─────────
  const mutateOptions = (uid: number, fn: (opts: string[]) => string[]) =>
    setFields(prev => prev.map(f => (f.uid === uid ? { ...f, options: fn(f.options ?? []) } : f)));

  const addOption = (uid: number) => mutateOptions(uid, o => [...o, `Option ${o.length + 1}`]);
  const setOption = (uid: number, i: number, v: string) =>
    mutateOptions(uid, o => o.map((x, idx) => (idx === i ? v : x)));
  const removeOption = (uid: number, i: number) =>
    mutateOptions(uid, o => o.filter((_, idx) => idx !== i));

  // When a field becomes a Choices type with no options yet, seed a couple so
  // the live example isn't empty.
  const changeFieldType = (uid: number, type: string) =>
    setFields(prev =>
      prev.map(f => {
        if (f.uid !== uid) return f;
        const next: DraftField = { ...f, type };
        if (CHOICE_TYPES.has(type) && (!f.options || f.options.length === 0)) {
          next.options = ['Option 1', 'Option 2'];
        }
        return next;
      }),
    );

  /* ── Image (web file picker → presigned upload) ─────────── */

  const pickImage = () => {
    if (Platform.OS !== 'web') return; // image control only rendered on web
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setImagePreview(URL.createObjectURL(file));
      setImageBusy(true);
      try {
        const key = await uploadTypeImage(file);
        setImageKey(key);
      } catch (err) {
        setImagePreview(undefined);
        handleApiError(err, 'Could not upload image');
      } finally {
        setImageBusy(false);
      }
    };
    input.click();
  };

  const clearImage = () => {
    setImageKey(undefined);
    setImagePreview(undefined);
  };

  /* ── Create ─────────────────────────────────────────────── */

  const trimmedName = name.trim();
  const hasEmptyLabel = fields.some(f => !f.label.trim());
  const canCreate = !!trimmedName && !hasEmptyLabel && !imageBusy && !creating;

  const handleCreate = async () => {
    if (!canCreate || creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    try {
      const created = await createItemType({ name: trimmedName, imageKey });
      const seen = new Set<string>();
      const failed: string[] = [];
      let ok = 0;
      // Sequential so field keys de-dupe deterministically and the order is kept.
      for (const f of fields) {
        const fieldKey = uniqueKey(seen, f.label);
        try {
          await addItemTypeField(created.id, {
            fieldKey,
            fieldType: f.type as any,
            isRequired: f.required,
          });
          ok++;
        } catch {
          failed.push(f.label.trim() || fieldKey);
        }
      }
      setResult({ created, total: fields.length, ok, failed });
    } catch (err) {
      // Close the confirm overlay so the customise step + name field are visible,
      // then surface the failure via the toast (it renders above this modal).
      setConfirmCreate(false);
      handleApiError(err, 'Could not create item type');
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const finish = (manage: boolean) => {
    const created = result?.created;
    setResult(null);
    onClose();
    if (created) onCreated(created, { manage });
  };

  /* ── Render helpers ─────────────────────────────────────── */

  const cardBase: ViewStyle = {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    padding: 16,
    backgroundColor: colors.card,
    ...(shadows.sm as any),
    ...Platform.select({
      web: { cursor: 'pointer', transition: 'border-color 100ms, box-shadow 100ms' } as ViewStyle,
      default: {},
    }),
  };

  // Premium primary button style (paired with hoverLift). The themed Button
  // already renders an adaptive ink/paper primary, so this only adds a shadow.
  const blackBtnStyle: ViewStyle = {
    ...Platform.select({
      web: { boxShadow: '0 1px 2px rgba(17,24,39,0.18)', transition: 'all 160ms ease' } as any,
      default: {},
    }),
  };

  // ── Step 1: template picker ──
  const step1Footer = (
    <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
      <Button onPress={onClose}>Cancel</Button>
      <Button
        kind="accent"
        icon="arrow-r"
        iconRight
        disabled={(!selId && !selTypeId) || preparing}
        onPress={continueToCustomize}
      >
        {preparing ? 'Loading…' : 'Continue'}
      </Button>
    </View>
  );

  const step1 = (
    <>
      <Alert tone="info" icon="sparkles" title="Pick a starting point — not a cage">
        Templates are pre-built field sets. You can rename it, add or remove fields, and set an image
        on the next step before anything is created.
      </Alert>
      <View style={{ marginTop: 16, flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        {SK.templates.map((t: Template) => {
          const isActive = selId === t.id;
          return (
            <TouchableOpacity
              key={t.id}
              activeOpacity={0.85}
              onPress={() => { setSelId(t.id); setSelTypeId(null); }}
              style={[
                cardBase,
                {
                  minWidth: 200,
                  flex: 1,
                  borderColor: isActive ? colors.accent : colors.line,
                  backgroundColor: isActive ? colors.accentSoft : colors.card,
                  ...(isActive ? (shadows.base as any) : {}),
                },
              ]}
            >
              <Text style={{ fontSize: 28, marginBottom: 10 }}>{t.emoji}</Text>
              <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.lg, marginBottom: 4, color: colors.ink, fontFamily: theme.font.sans }}>
                {t.name}
              </Text>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, marginBottom: 10 }}>
                {t.industry}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                {t.fields.slice(0, 4).map(f => (
                  <Chip key={f.label} tone="outline">{f.label}</Chip>
                ))}
                {t.fields.length > 4 && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, alignSelf: 'center' }}>
                    +{t.fields.length - 4}
                  </Text>
                )}
                {t.fields.length === 0 && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
                    Start from scratch
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ── Your types — the tenant's existing types, reusable as templates ── */}
      {myTypes.length > 0 && (
        <>
          <Text style={{ marginTop: 22, marginBottom: 10, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink2, fontFamily: theme.font.sans, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Your types
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
            {myTypes.map(mt => {
              const isActive = selTypeId === mt.id;
              const img = (mt as any).imageUrl as string | undefined;
              return (
                <TouchableOpacity
                  key={mt.id}
                  activeOpacity={0.85}
                  onPress={() => { setSelTypeId(mt.id); setSelId(null); }}
                  style={[
                    cardBase,
                    {
                      width: 320,
                      maxWidth: '100%',
                      minHeight: 160,
                      padding: 18,
                      borderColor: isActive ? colors.accent : colors.line,
                      backgroundColor: isActive ? colors.accentSoft : colors.card,
                      ...(isActive ? (shadows.base as any) : {}),
                    },
                  ]}
                >
                  {/* Sample image loaded from the DB (falls back to 📦). */}
                  {img ? (
                    <Image
                      source={{ uri: img }}
                      style={{ width: 56, height: 56, borderRadius: radii.r2, marginBottom: 12, backgroundColor: colors.paper2 }}
                      resizeMode="cover"
                    />
                  ) : (
                    <Text style={{ fontSize: 34, marginBottom: 12 }}>📦</Text>
                  )}
                  <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.lg, marginBottom: 4, color: colors.ink, fontFamily: theme.font.sans }}>
                    {mt.name}
                  </Text>
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>
                    {mt.description || 'Your existing type'}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      )}
    </>
  );

  // ── Step 2: customise ──
  const step2Footer = (
    <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
      <Button icon="chev-l" onPress={() => setStep(1)}>Back</Button>
      <Button kind="primary" icon="check" hoverLift disabled={!canCreate} onPress={() => setConfirmCreate(true)} style={blackBtnStyle}>
        {creating ? 'Creating…' : 'Create type'}
      </Button>
    </View>
  );

  const labelStyle = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: colors.ink2,
    fontFamily: theme.font.sans,
    marginBottom: 6,
  };

  /* ── Live example renderer — how ONE field looks to the end user.
        Shared by the left "Example" panel and the right "Preview" panel so
        there's a single source of truth for every field type. ───────────── */
  const exShell: ViewStyle = {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    backgroundColor: colors.card,
    paddingHorizontal: 12,
    paddingVertical: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  };
  const exMuted = (t: string) => (
    <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>{t}</Text>
  );
  const exInk = (t: string) => (
    <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans }}>{t}</Text>
  );

  const renderExample = (field: DraftField): React.ReactNode => {
    const opts = field.options && field.options.length ? field.options : ['Option 1', 'Option 2'];
    switch (field.type) {
      case 'NUMBER':
        return <View style={exShell}>{exMuted('0')}</View>;
      case 'CURRENCY':
        return (
          <View style={exShell}>
            <Icon name="rupee" size={14} color={colors.ink3} />
            {exMuted('0.00')}
          </View>
        );
      case 'DATE':
        return (
          <View style={[exShell, { justifyContent: 'space-between' }]}>
            {exMuted('DD / MM / YYYY')}
            <Icon name="calendar2" size={15} color={colors.ink3} />
          </View>
        );
      case 'BOOLEAN':
        return (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accent }}>
              <Text style={{ fontSize: fontSize.sm, color: colors.accentInk, fontFamily: theme.font.sans }}>Yes</Text>
            </View>
            <View style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>No</Text>
            </View>
          </View>
        );
      case 'MEDIA':
        return (
          <View style={{ width: 92, height: 92, borderRadius: radii.r3, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.line, backgroundColor: colors.paper2, alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Icon name="image" size={22} color={colors.ink3} />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>Upload</Text>
          </View>
        );
      case 'ENUM':
        return (
          <View style={{ gap: 8 }}>
            <View style={[exShell, { justifyContent: 'space-between' }]}>
              {exInk(opts[0])}
              <Icon name="chev-d" size={14} color={colors.ink3} />
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {opts.map((o, i) => (
                <View key={i} style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink2, fontFamily: theme.font.sans }}>{o}</Text>
                </View>
              ))}
            </View>
          </View>
        );
      case 'MULTI_SELECT':
        return (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {opts.map((o, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, backgroundColor: i < 2 ? colors.accentSoft : colors.paper2, borderWidth: 1, borderColor: i < 2 ? colors.accent : colors.line }}>
                <Text style={{ fontSize: fontSize.xs, color: i < 2 ? colors.accentInk : colors.ink2, fontFamily: theme.font.sans }}>{o}</Text>
                {i < 2 && <Text style={{ fontSize: fontSize.xs, color: colors.accentInk, fontFamily: theme.font.sans }}>×</Text>}
              </View>
            ))}
          </View>
        );
      case 'RICH_TEXT':
        return <View style={[exShell, { minHeight: 72, alignItems: 'flex-start' }]}>{exMuted('Write a longer description…')}</View>;
      case 'RELATION':
        return (
          <View style={[exShell, { justifyContent: 'space-between' }]}>
            {exMuted('Search to link…')}
            <Icon name="link" size={14} color={colors.ink3} />
          </View>
        );
      case 'GEO':
        return (
          <View style={[exShell, { justifyContent: 'space-between' }]}>
            {exMuted('Pick a location')}
            <Icon name="map-pin" size={15} color={colors.ink3} />
          </View>
        );
      case 'JSON':
        return (
          <View style={[exShell, { minHeight: 60, alignItems: 'flex-start' }]}>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: 'monospace' as any }}>{'{ "key": "value" }'}</Text>
          </View>
        );
      case 'TEXT':
      default:
        return <View style={exShell}>{exMuted('Type here…')}</View>;
    }
  };

  // Panel chrome shared by Example / Preview columns.
  const panelStyle: ViewStyle = {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r4,
    backgroundColor: colors.paper,
    padding: 18,
    gap: 16,
    ...(shadows.sm as any),
  };
  const panelLabel = (t: string) => (
    <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink3, fontFamily: theme.font.sans, textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {t}
    </Text>
  );

  // The field whose example fills the left panel (falls back to the first).
  const activeField = fields.find(f => f.uid === activeUid) ?? fields[0];

  // ── LEFT — the focused editor for the active field: a clear end-user
  //    example up top, and (for Choices) the options editor right below it.
  //    This is the ONLY place choices are edited. ──
  const examplePanelContent = activeField ? (
    <View style={{ gap: 18 }}>
      {/* Type badge */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View style={{ width: 34, height: 34, borderRadius: radii.r3, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={metaFor(activeField.type).icon} size={16} color={colors.accent} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans }} numberOfLines={1}>
            {activeField.label.trim() || 'Untitled field'}
          </Text>
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
            {metaFor(activeField.type).label} field
          </Text>
        </View>
      </View>

      {/* The end-user example */}
      <View style={{ gap: 8, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2, padding: 16 }}>
        <Text style={labelStyle as any}>{activeField.label.trim() || 'Field label'}</Text>
        {renderExample(activeField)}
        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, lineHeight: 18, marginTop: 4 }}>
          This is exactly how people will see “{activeField.label.trim() || 'this field'}” when they fill in the form.
        </Text>
      </View>

      {/* Options editor — only for Choices / Multi-select, edited here only */}
      {CHOICE_TYPES.has(activeField.type) && (
        <View style={{ gap: 10 }}>
          <Text style={labelStyle as any}>Choices</Text>
          {(activeField.options ?? []).map((opt, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, width: 18 }}>{i + 1}.</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Input
                  placeholder={`Option ${i + 1}`}
                  value={opt}
                  onChangeText={(v: string) => setOption(activeField.uid, i, v)}
                />
              </View>
              <TouchableOpacity
                onPress={() => removeOption(activeField.uid, i)}
                accessibilityLabel="Remove option"
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                style={{ width: 30, height: 30, borderRadius: radii.r3, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }) }}
              >
                <Icon name="trash" size={15} color={colors.ink3} />
              </TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity
            onPress={() => addOption(activeField.uid)}
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 11, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.line, borderRadius: radii.r3, ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }) }}
          >
            <Icon name="plus" size={14} color={colors.accent} />
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.accent, fontFamily: theme.font.sans }}>Add choice</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  ) : (
    <Empty title="Select a field">Click a field in the middle to edit it and see a live example here.</Empty>
  );

  // ── RIGHT — live preview of the whole type as an end user sees it.
  //    Each field carries its own delete control (the only place to remove). ──
  const previewPanelContent = (
    <View style={{ gap: 16 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <View style={{ width: 44, height: 44, borderRadius: radii.r3, overflow: 'hidden', backgroundColor: colors.paper2, alignItems: 'center', justifyContent: 'center' }}>
          {imagePreview ? (
            <Image source={{ uri: imagePreview }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
          ) : (
            <Text style={{ fontSize: 22 }}>📦</Text>
          )}
        </View>
        <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, flex: 1 }} numberOfLines={1}>
          {trimmedName || 'Untitled type'}
        </Text>
      </View>
      {fields.length === 0 ? (
        <Empty title="No fields yet">Your form preview shows up here.</Empty>
      ) : (
        <View style={{ gap: 16 }}>
          {fields.map(f => {
            const isActive = activeField?.uid === f.uid;
            return (
              <Pressable
                key={f.uid}
                onPress={() => setActiveUid(f.uid)}
                style={{ gap: 6, borderRadius: radii.r3, padding: 10, margin: -10, backgroundColor: isActive ? colors.accentSoft : 'transparent' }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans, flex: 1 }} numberOfLines={1}>
                    {f.label.trim() || 'Untitled field'}
                  </Text>
                  {f.required && <Text style={{ fontSize: fontSize.sm, color: colors.danger, fontFamily: theme.font.sans }}>*</Text>}
                  <TouchableOpacity
                    onPress={() => setDelUid(f.uid)}
                    accessibilityLabel="Delete field"
                    hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    style={{ width: 28, height: 28, borderRadius: radii.r2, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }) }}
                  >
                    <Icon name="trash" size={15} color={colors.ink3} />
                  </TouchableOpacity>
                </View>
                {renderExample(f)}
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );

  // ── CENTER — the field builder (name + image + field list). Editing details
  //    (choices) happens on the LEFT; deleting happens on the RIGHT. ──
  const fieldsPanelContent = (
    <View style={{ gap: 16 }}>
      {/* Name + image */}
      <View style={{ flexDirection: stackRow ? 'column' : 'row', gap: stackRow ? 14 : 20, alignItems: stackRow ? 'stretch' : 'flex-end' }}>
        {Platform.OS === 'web' && (
          <View style={stackRow ? { alignItems: 'flex-start' } : undefined}>
            <Text style={labelStyle as any}>Image</Text>
            <TouchableOpacity
              onPress={pickImage}
              activeOpacity={0.85}
              style={{
                width: 84,
                height: 84,
                borderRadius: radii.r3,
                borderWidth: 1,
                borderColor: colors.line,
                borderStyle: imagePreview ? 'solid' : 'dashed',
                backgroundColor: colors.paper2,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
              }}
            >
              {imageBusy ? (
                <ActivityIndicator color={colors.accent} />
              ) : imagePreview ? (
                <Image source={{ uri: imagePreview }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
              ) : (
                <View style={{ alignItems: 'center', gap: 4 }}>
                  <Icon name="image" size={22} color={colors.ink3} />
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>Upload</Text>
                </View>
              )}
              <View style={{ position: 'absolute', right: 4, bottom: 4, width: 24, height: 24, borderRadius: 12, backgroundColor: colors.accent, borderWidth: 2, borderColor: colors.card, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="upload" size={11} color={colors.white} />
              </View>
            </TouchableOpacity>
            {!!imagePreview && !imageBusy && (
              <TouchableOpacity onPress={clearImage} style={{ marginTop: 4 }}>
                <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontFamily: theme.font.sans, textAlign: 'center' }}>
                  Remove
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={labelStyle as any}>Type name</Text>
          <Input
            placeholder="e.g. Auto Listing"
            value={name}
            onChangeText={(v: string) => setName(v)}
            autoFocus
          />
        </View>
      </View>

      {/* Fields header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {panelLabel('Fields')}
          {fields.length > 0 && (
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
              · {fields.length} field{fields.length === 1 ? '' : 's'}
            </Text>
          )}
        </View>
        <Button kind="primary" size="sm" icon="plus" hoverLift onPress={addField} style={blackBtnStyle}>
          Add field
        </Button>
      </View>

      {fields.length === 0 ? (
        <Empty title="No fields yet">Add your first field to describe this type.</Empty>
      ) : (
        // One continuous list — hairline-divided rows, no per-row boxes.
        <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.r4, overflow: 'hidden', backgroundColor: colors.card }}>
          {fields.map((f, idx) => {
            const meta = metaFor(f.type);
            const isActive = activeField?.uid === f.uid;
            const last = idx === fields.length - 1;
            const ghostName: any = {
              minWidth: 0,
              fontSize: 15,
              fontWeight: '600',
              color: colors.ink,
              fontFamily: theme.font.sans,
              paddingVertical: 4,
              ...(stackRow ? { width: '100%' } : { flex: 1 }),
              ...(Platform.OS === 'web' ? { outlineStyle: 'none', borderWidth: 0 } : {}),
            };
            const controls = (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, justifyContent: stackRow ? 'space-between' : 'flex-end' }}>
                {/* Type toggle — soft, borderless chip */}
                <TouchableOpacity
                  onPress={() => { setActiveUid(f.uid); setShowAdvanced(false); setTypePickerUid(f.uid); }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: radii.r3,
                    paddingHorizontal: 10,
                    paddingVertical: 7,
                    backgroundColor: colors.paper2,
                    ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
                  }}
                >
                  <Icon name={meta.icon} size={14} color={colors.ink2} />
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans }}>{meta.label}</Text>
                  <Icon name="chev-d" size={12} color={colors.ink3} />
                </TouchableOpacity>

                {/* Required */}
                <Switch label="Required" checked={f.required} onChange={() => patchField(f.uid, { required: !f.required })} />
              </View>
            );
            return (
              <Pressable
                key={f.uid}
                onPress={() => setActiveUid(f.uid)}
                style={{
                  backgroundColor: isActive ? colors.accentSoft : colors.card,
                  borderLeftWidth: 3,
                  borderLeftColor: isActive ? colors.accent : 'transparent',
                  borderBottomWidth: last ? 0 : 1,
                  borderBottomColor: colors.line,
                }}
              >
                <View style={{ paddingVertical: 14, paddingHorizontal: 16, flexDirection: stackRow ? 'column' : 'row', alignItems: stackRow ? 'stretch' : 'center', gap: stackRow ? 10 : 12 }}>
                  <TextInput
                    value={f.label}
                    placeholder="Field name"
                    placeholderTextColor={colors.ink3}
                    onFocus={() => setActiveUid(f.uid)}
                    onChangeText={(v: string) => patchField(f.uid, { label: v })}
                    style={ghostName}
                  />
                  {controls}
                </View>
                {/* "Choices" hint — editing happens in the left panel */}
                {CHOICE_TYPES.has(f.type) && (
                  <View style={{ paddingHorizontal: 16, paddingBottom: 12, marginTop: -4 }}>
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
                      {(f.options?.length ?? 0)} choice{(f.options?.length ?? 0) === 1 ? '' : 's'} · edit on the left
                    </Text>
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Add field — single dashed affordance */}
      <TouchableOpacity
        onPress={addField}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingVertical: 13,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: colors.line,
          borderRadius: radii.r4,
          backgroundColor: 'transparent',
          ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
        }}
      >
        <Icon name="plus" size={15} color={colors.ink2} />
        <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans }}>Add field</Text>
      </TouchableOpacity>

      {hasEmptyLabel && (
        <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontFamily: theme.font.sans }}>
          Every field needs a name before you can create the type.
        </Text>
      )}
    </View>
  );

  // Left editor panel — fixed height + internal scroll so it stays pinned in
  // place (sticky on web) while the centre list scrolls.
  const leftPanel = (
    <View style={[panelStyle, { height: threeCol ? colHeight : undefined, gap: 14 }]}>
      {panelLabel('Live example')}
      {threeCol ? (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 4 }} showsVerticalScrollIndicator={false}>
          {examplePanelContent}
        </ScrollView>
      ) : (
        examplePanelContent
      )}
    </View>
  );

  const centerPanel = <View style={[panelStyle, { backgroundColor: colors.card, minHeight: threeCol ? colHeight : undefined }]}>{fieldsPanelContent}</View>;

  const rightPanel = (
    <View style={[panelStyle, { minHeight: threeCol ? colHeight : undefined, gap: 14 }]}>
      {panelLabel('Preview')}
      {previewPanelContent}
    </View>
  );

  const step2 = (
    <View style={{ flexDirection: threeCol ? 'row' : 'column', gap: 16, alignItems: 'flex-start' }}>
      {threeCol ? (
        <>
          {/* Left stays put while the rest scrolls (web sticky). */}
          <View
            style={{
              flex: 0.9,
              minWidth: 0,
              ...Platform.select({ web: { position: 'sticky', top: 0 } as any, default: {} }),
            }}
          >
            {leftPanel}
          </View>
          <View style={{ flex: 1.4, minWidth: 0, alignSelf: 'stretch' }}>{centerPanel}</View>
          <View style={{ flex: 1, minWidth: 0, alignSelf: 'stretch' }}>{rightPanel}</View>
        </>
      ) : (
        <>
          <View style={{ width: '100%' }}>{centerPanel}</View>
          <View style={{ width: '100%' }}>{leftPanel}</View>
          <View style={{ width: '100%' }}>{rightPanel}</View>
        </>
      )}
    </View>
  );

  // ── Type picker (friendly) ──
  const pickerField = fields.find(f => f.uid === typePickerUid);
  const typePicker = (
    <Modal
      open={typePickerUid !== null}
      onClose={() => setTypePickerUid(null)}
      size="sm"
      title="What kind of info?"
    >
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {(showAdvanced ? [...FRIENDLY_TYPES, ...ADVANCED_TYPES] : FRIENDLY_TYPES).map(ft => {
          const isActive = pickerField?.type === ft.type;
          return (
            <TouchableOpacity
              key={ft.type}
              onPress={() => {
                if (typePickerUid !== null) changeFieldType(typePickerUid, ft.type);
                setTypePickerUid(null);
              }}
              style={{
                borderWidth: 1,
                borderColor: isActive ? colors.accent : colors.line,
                borderRadius: radii.r2,
                padding: 12,
                width: '30%',
                alignItems: 'center',
                gap: 6,
                backgroundColor: isActive ? colors.accentSoft : colors.card,
                ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }),
              }}
            >
              <Icon name={ft.icon} size={18} color={isActive ? colors.accentInk : colors.ink2} />
              <Text style={{ fontSize: fontSize.sm, color: isActive ? colors.accentInk : colors.ink2, fontFamily: theme.font.sans, textAlign: 'center' }}>
                {ft.friendlyLabel}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {!showAdvanced && (
        <TouchableOpacity onPress={() => setShowAdvanced(true)} style={{ marginTop: 12, alignSelf: 'center' }}>
          <Text style={{ fontSize: fontSize.sm, color: colors.accent, fontFamily: theme.font.sans }}>
            More types…
          </Text>
        </TouchableOpacity>
      )}
    </Modal>
  );

  // ── Delete confirmation — rendered INSIDE the wizard modal as a fixed
  // overlay (not a second RN Modal). A nested RN Modal would hide the wizard
  // behind it on web; this keeps the confirmation on top of the card. ──
  const deleteOverlay = delUid !== null && (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15,23,42,0.75)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <Pressable
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onPress={() => setDelUid(null)}
      />
      <View
        style={{
          width: 340,
          maxWidth: '100%',
          backgroundColor: colors.card,
          borderRadius: 20,
          overflow: 'hidden',
          ...(shadows.lg as any),
        }}
      >
        <View style={{ height: 4, width: '100%', backgroundColor: colors.danger, opacity: 0.85 }} />
        <View style={{ paddingTop: 20, paddingHorizontal: 18, paddingBottom: 16, alignItems: 'center' }}>
          <Text style={{ fontSize: 17, fontWeight: '800', fontFamily: theme.font.sans, color: colors.ink, textAlign: 'center', marginBottom: 8 }}>
            Delete field?
          </Text>
          <Text style={{ fontSize: 13, fontFamily: theme.font.sans, color: colors.ink3, textAlign: 'center', lineHeight: 19 }}>
            This field will be removed from the new type. You can add it again before creating.
          </Text>
        </View>
        <View style={{ flexDirection: 'row', padding: 14, gap: 10, backgroundColor: colors.paper2 }}>
          <Pressable
            onPress={() => setDelUid(null)}
            style={({ pressed }) => ({
              flex: 1,
              height: 42,
              borderRadius: 12,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: colors.paper3,
              opacity: pressed ? 0.85 : 1,
            })}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', fontFamily: theme.font.sans, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={() => { if (delUid !== null) removeField(delUid); setDelUid(null); }}
            style={({ pressed }) => ({
              flex: 1,
              height: 42,
              borderRadius: 12,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: colors.danger,
              opacity: pressed ? 0.85 : 1,
            })}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', fontFamily: theme.font.sans, color: colors.white }}>Delete</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );

  // ── Create confirmation — asked in-screen before anything is created. ──
  const createOverlay = confirmCreate && (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15,23,42,0.75)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <Pressable
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        onPress={() => !creating && setConfirmCreate(false)}
      />
      <View style={{ width: 360, maxWidth: '100%', backgroundColor: colors.card, borderRadius: 20, overflow: 'hidden', ...(shadows.lg as any) }}>
        <View style={{ height: 4, width: '100%', backgroundColor: colors.accent }} />
        <View style={{ paddingTop: 22, paddingHorizontal: 20, paddingBottom: 16, alignItems: 'center' }}>
          <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <Icon name="check" size={22} color={colors.accent} />
          </View>
          <Text style={{ fontSize: 17, fontWeight: '800', fontFamily: theme.font.sans, color: colors.ink, textAlign: 'center', marginBottom: 8 }}>
            Create this type?
          </Text>
          <Text style={{ fontSize: 13, fontFamily: theme.font.sans, color: colors.ink3, textAlign: 'center', lineHeight: 19 }}>
            “{trimmedName || 'Untitled'}” will be created with {fields.length} field{fields.length === 1 ? '' : 's'}
            {imageKey ? ' and an image' : ''}. You can keep editing afterwards.
          </Text>
        </View>
        <View style={{ flexDirection: 'row', padding: 14, gap: 10, backgroundColor: colors.paper2 }}>
          <Pressable
            onPress={() => !creating && setConfirmCreate(false)}
            style={({ pressed }) => ({ flex: 1, height: 42, borderRadius: 12, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.paper3, opacity: pressed ? 0.85 : 1 })}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', fontFamily: theme.font.sans, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            disabled={creating}
            onPress={async () => { await handleCreate(); setConfirmCreate(false); }}
            style={({ pressed }) => ({ flex: 1, height: 42, borderRadius: 12, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.ink, opacity: pressed || creating ? 0.85 : 1 })}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', fontFamily: theme.font.sans, color: colors.paper }}>
              {creating ? 'Creating…' : 'Create'}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );

  return (
    <>
      <Modal
        open={open && !result}
        onClose={onClose}
        size={step === 1 ? 'wide' : undefined}
        fullScreen={step === 2}
        title={step === 1 ? 'Start from a template' : 'Customize your type'}
        footer={step === 1 ? step1Footer : step2Footer}
        overlay={deleteOverlay || createOverlay}
      >
        {step === 1 ? step1 : step2}
      </Modal>

      {typePicker}

      <SuccessDialog
        visible={!!result}
        tone={result && result.failed.length ? 'warning' : 'success'}
        title={result && result.failed.length ? 'Created with warnings' : 'Type created'}
        message={result ? `"${result.created.name}" is ready.` : ''}
        summary={
          result
            ? [
                result.total === 0
                  ? 'No fields yet — add them next'
                  : `${result.ok} of ${result.total} field${result.total === 1 ? '' : 's'} added`,
                ...(imageKey ? ['Image attached'] : []),
                ...(result.failed.length ? [`Couldn't add: ${result.failed.join(', ')}`] : []),
              ]
            : undefined
        }
        primaryAction={{ label: 'Manage fields', onPress: () => finish(true) }}
        secondaryAction={{ label: 'Done', onPress: () => finish(false) }}
        onClose={() => finish(false)}
      />
    </>
  );
}
